import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, FileSignature, Loader2, Printer, XCircle } from 'lucide-react';
import { API } from '../lib/api';

// The page a customer opens from "please review and sign". No login: the link
// itself is signed by the server. English and Spanish, phone first.
const T = {
  en: {
    loading: 'Loading your document…', invalid: 'This signing link is not valid. Please contact the office for a new one.',
    preparedFor: 'Prepared for', item: 'Description', qty: 'Qty', rate: 'Rate', amount: 'Amount', total: 'Total',
    service: 'Service', frequency: 'Frequency', price: 'Price', start: 'Start date', expires: 'Valid until',
    yourName: 'Type your full name', drawHere: 'Sign here with your finger or mouse', clear: 'Clear',
    agree: 'I have read this document and agree to it. My typed name and drawn signature are my electronic signature.',
    sign: 'Sign and accept', decline: 'Decline', declineConfirm: 'Decline this document?', reason: 'Reason (optional)',
    signedTitle: 'Signed — thank you!', signedBody: 'A copy is saved with your account. You can print it for your records.',
    declinedTitle: 'Declined', declinedBody: 'We let the office know. Call us if you have questions.',
    print: 'Print a copy', signedBy: 'Signed by', on: 'on', needName: 'Type your full name', needSig: 'Please draw your signature', needAgree: 'Please tick the box to agree',
  },
  es: {
    loading: 'Cargando su documento…', invalid: 'Este enlace de firma no es válido. Comuníquese con la oficina para recibir uno nuevo.',
    preparedFor: 'Preparado para', item: 'Descripción', qty: 'Cant.', rate: 'Precio', amount: 'Importe', total: 'Total',
    service: 'Servicio', frequency: 'Frecuencia', price: 'Precio', start: 'Fecha de inicio', expires: 'Válido hasta',
    yourName: 'Escriba su nombre completo', drawHere: 'Firme aquí con el dedo o el ratón', clear: 'Borrar',
    agree: 'He leído este documento y lo acepto. Mi nombre escrito y mi firma dibujada son mi firma electrónica.',
    sign: 'Firmar y aceptar', decline: 'Rechazar', declineConfirm: '¿Rechazar este documento?', reason: 'Motivo (opcional)',
    signedTitle: '¡Firmado — gracias!', signedBody: 'Se guardó una copia en su cuenta. Puede imprimirla para sus registros.',
    declinedTitle: 'Rechazado', declinedBody: 'Avisamos a la oficina. Llámenos si tiene preguntas.',
    print: 'Imprimir una copia', signedBy: 'Firmado por', on: 'el', needName: 'Escriba su nombre completo', needSig: 'Por favor dibuje su firma', needAgree: 'Marque la casilla para aceptar',
  },
};
const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

function Pad({ onChange, clearLabel }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: ((p.clientX - r.left) / r.width) * ref.current.width, y: ((p.clientY - r.top) / r.height) * ref.current.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const c = ref.current.getContext('2d'); c.beginPath(); c.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const c = ref.current.getContext('2d');
    c.lineWidth = 3; c.lineCap = 'round'; c.strokeStyle = '#0f172a';
    c.lineTo(x, y); c.stroke();
    drawn.current = true;
  };
  const end = () => { if (drawing.current && drawn.current) onChange(ref.current.toDataURL('image/png')); drawing.current = false; };
  const clear = () => { ref.current.getContext('2d').clearRect(0, 0, ref.current.width, ref.current.height); drawn.current = false; onChange(''); };

  return (
    <div>
      <canvas ref={ref} width={700} height={220} className="w-full h-40 rounded-lg border-2 border-dashed border-slate-300 bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="sign-pad" />
      <button type="button" onClick={clear} className="mt-1 text-xs text-slate-500 underline">{clearLabel}</button>
    </div>
  );
}

export default function SignDocument() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get('t') || '';
  const [lang, setLang] = useState((navigator.language || '').startsWith('es') ? 'es' : 'en');
  const L = T[lang];
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [sig, setSig] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API}/public/esign/${encodeURIComponent(id)}?t=${encodeURIComponent(t)}`)
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || 'invalid'); return d; })
      .then(setDoc).catch((e) => setError(e.message));
  }, [id, t]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    const r = await fetch(`${API}/public/esign/${encodeURIComponent(id)}/${path}?t=${encodeURIComponent(t)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || 'Error');
    return d;
  };

  const sign = async () => {
    if (!name.trim()) { setMsg(L.needName); return; }
    if (!sig) { setMsg(L.needSig); return; }
    if (!agree) { setMsg(L.needAgree); return; }
    setBusy(true); setMsg('');
    try { await post('sign', { signerName: name.trim(), signature: sig, agree: true }); load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const decline = async () => {
    if (!window.confirm(L.declineConfirm)) return;
    const reason = window.prompt(L.reason) || '';
    setBusy(true);
    try { await post('decline', { reason }); load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const s = doc?.snapshot || {};
  const co = s.company || {};
  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 print:bg-white print:p-0">
      <div className="max-w-2xl mx-auto bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden print:border-0 print:shadow-none">
        <div className="bg-[#0d2818] text-white p-5 flex justify-between items-start gap-3">
          <div>
            <div className="text-lg font-extrabold">{co.companyName || 'Xpertpests Solutions'}</div>
            <div className="text-xs text-white/70">{[co.phone1, co.email].filter(Boolean).join(' · ')}</div>
          </div>
          <div className="flex gap-1 print:hidden">
            {['en', 'es'].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-2 py-1 rounded text-xs font-bold ${lang === l ? 'bg-white text-[#0d2818]' : 'bg-white/10'}`}>{l.toUpperCase()}</button>)}
          </div>
        </div>

        {error ? <div className="p-8 text-center text-slate-600"><XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />{L.invalid}<div className="text-xs text-slate-400 mt-2">{error}</div></div>
          : !doc ? <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />{L.loading}</div> : (
            <div className="p-5 space-y-5">
              <div>
                <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#8BC53F]" />{doc.title}</h1>
                <div className="text-sm text-slate-600 mt-1">{L.preparedFor}: <b>{s.customer?.name}</b>{s.customer?.address ? ` · ${s.customer.address}` : ''}</div>
                {s.expires && <div className="text-xs text-slate-500">{L.expires}: {s.expires}</div>}
              </div>

              {doc.kind === 'estimate' && (
                <div>
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 border-b"><tr><th className="text-left py-2">{L.item}</th><th className="text-right">{L.qty}</th><th className="text-right">{L.rate}</th><th className="text-right">{L.amount}</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {(s.items || []).map((i, k) => <tr key={k}><td className="py-2 pr-2">{i.desc}</td><td className="text-right">{i.qty}</td><td className="text-right">{money(i.rate)}</td><td className="text-right">{money(i.qty * i.rate)}</td></tr>)}
                    </tbody>
                  </table>
                  <div className="flex justify-between border-t-2 border-slate-900 mt-2 pt-2 font-extrabold"><span>{L.total}</span><span>{money(s.total)}</span></div>
                  {s.notes && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{s.notes}</p>}
                  {s.terms && <p className="text-xs text-slate-500 mt-3 whitespace-pre-wrap">{s.terms}</p>}
                </div>
              )}
              {doc.kind === 'agreement' && (
                <div className="space-y-3">
                  {(s.service || s.price) ? (
                    <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 rounded-lg p-3">
                      {s.service && <div><span className="text-slate-500">{L.service}:</span> <b>{s.service}</b></div>}
                      {s.frequency && <div><span className="text-slate-500">{L.frequency}:</span> <b>{s.frequency}</b></div>}
                      {s.price ? <div><span className="text-slate-500">{L.price}:</span> <b>{money(s.price)}</b></div> : null}
                      {s.startDate && <div><span className="text-slate-500">{L.start}:</span> <b>{s.startDate}</b></div>}
                    </div>
                  ) : null}
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{s.text}</div>
                </div>
              )}

              {doc.status === 'signed' ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="sign-done">
                  <div className="font-bold text-emerald-900 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" />{L.signedTitle}</div>
                  <p className="text-sm text-emerald-800 print:hidden">{L.signedBody}</p>
                  {doc.signature && <img src={doc.signature} alt="Signature" className="h-20 bg-white rounded border border-emerald-100" />}
                  <div className="text-xs text-slate-600">{L.signedBy} <b>{doc.signerName}</b> {L.on} {new Date(doc.signedAt).toLocaleString()}</div>
                  <div className="text-[10px] text-slate-400 break-all">Document fingerprint (SHA-256): {doc.docHash}</div>
                  <button onClick={() => window.print()} className="print:hidden inline-flex items-center gap-1.5 text-sm font-semibold text-[#0d2818] underline"><Printer className="w-4 h-4" />{L.print}</button>
                </div>
              ) : doc.status === 'declined' ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4"><div className="font-bold text-slate-800">{L.declinedTitle}</div><p className="text-sm text-slate-600">{L.declinedBody}</p></div>
              ) : (
                <div className="space-y-3 print:hidden">
                  <label className="block text-sm font-semibold text-slate-700">{L.yourName}
                    <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-base" autoComplete="name" data-testid="sign-name" />
                  </label>
                  <div><div className="text-sm font-semibold text-slate-700 mb-1">{L.drawHere}</div><Pad onChange={setSig} clearLabel={L.clear} /></div>
                  <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#8BC53F]" data-testid="sign-agree" />{L.agree}</label>
                  {msg && <p className="text-sm text-red-600">{msg}</p>}
                  <button onClick={sign} disabled={busy} className="w-full h-12 rounded-lg bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-bold disabled:opacity-60" data-testid="sign-submit">{busy ? '…' : L.sign}</button>
                  <button onClick={decline} disabled={busy} className="w-full text-sm text-slate-500 underline">{L.decline}</button>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}
